import { toast } from 'react-toastify'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  InputWithAddon,
  ResizibleTextArea,
  SimpleCustomSelect,
  CheckboxWithDescription,
  InputWithValidationError,
  RadioListWithDescription
} from 'components/FormInputs'
import { FormSection } from 'components/Form'
import { PageHeader } from 'components/PageHeader'
import {
  billingTypes,
  currencies,
  intervals,
  hardwareNatures,
  connectivityNatures,
  usageTypes,
  fineModes,
  yesOrNo
} from 'components/FormInputs/options'

import { Nature, Plan, PlanMeta, PlanType, Product } from 'models'
import { Errors } from 'types'
import { ReadResult } from 'interfaces/queryOptions'
import { currency, formatCurrency } from 'utils'
import { check } from 'utils/error'
import { validate } from '../validation'
import { create } from '../services/api'
import { cleanAmount } from 'utils'
import { listCountries } from 'modules/Staff/services/api'
import { ProductSearch } from '../components/Searchs/Product'

const hardwareOrConnectivityActiveProductFilters = [
  { key: 'type', op: 'in', value: ['hardware', 'connectivity'] },
  { key: 'active', op: 'eq', value: true }
]

export function Create() {
  const navigate = useNavigate()

  interface CountryOption {
    value: number
    label: string
  }

  const [countries, setCountries] = useState<CountryOption[]>([])
  const [selectedCountry, setSelectedCountry] = useState('')

  const [productId, setProductId] = useState<number | ''>('')
  const [productType, setProductType] = useState<PlanType | ''>('')

  useEffect(() => {
    const fetchCountries = async () => {
      const countries = await listCountries({
        attributes: ['code', 'name', 'local_name']
      })

      setCountries(
        countries.data.map((country) => {
          return {
            value: country.id,
            label: country.attributes.local_name ?? ''
          }
        })
      )
    }

    fetchCountries()
  }, [])

  const [plan, setPlan] = useState<Record<string, any>>({
    amount: 0,
    active: false,
    currency: currencies[0].value,
    billing_type: billingTypes[0].value,
    interval: intervals[0].value,
    interval_count: 1,
    duration: 1,
    renewable: false,
    recurring: true,
    restricted: false,
    guarantors: false,
    logistics: true,
    fiscal_data: true,
    prorata: true,
    nature: connectivityNatures[0].value,
    usage_type: usageTypes[0].value,
    fine_mode: 'fixed',
    fine_amount: '0,00',
    max_installments: 1
  })

  const [installmentFriendly, setHardwareInstallmentFriendly] = useState('no')
  const [recurring, setRecurring] = useState('yes')

  const [errors, setErrors] = useState<Errors>({})

  const validateFieds = useCallback(
    (plan: Record<string, any>): boolean => {
      const isNameFilled = !!(
        Object.prototype.hasOwnProperty.call(plan, 'name') && plan.name !== null
      )
      const isAmountFilled = !!(
        Object.prototype.hasOwnProperty.call(plan, 'amount') &&
        plan.amount !== 0
      )

      if (!isNameFilled || !isAmountFilled) {
        setErrors({
          ...errors,
          name: !isNameFilled ? 'O campo deve ser preenchido' : null,
          amount: !isAmountFilled ? 'Valor inválido' : null
        })

        return true
      }
      return false
    },
    [errors]
  )

  const onSave = useCallback(async () => {
    if (!plan) return

    if (!selectedCountry) {
      toast.error('O país do plano deve ser selecionado')
      return
    }

    if (!productId) {
      toast.error('O produto do plano deve ser selecionado')
      return
    }

    const transformAttributes = (
      attributes: Record<string, any>,
      recurring: boolean
    ): Record<string, any> => {
      const { fine_mode, fine_amount } = attributes

      return {
        fine_mode: recurring
          ? fine_amount === ''
            ? 'fixed'
            : fine_mode
          : null,
        fine_amount: recurring
          ? fine_amount === ''
            ? 0
            : fine_mode === null
            ? 0
            : cleanAmount(fine_amount)
          : null
      }
    }

    if (validateFieds(plan) || check(errors)) {
      return
    }

    const otherAttributes = transformAttributes(
      plan,
      recurring === 'yes' ? true : false
    )

    if (
      recurring === 'no' &&
      (!plan.max_installments || installmentFriendly === 'no')
    ) {
      plan.max_installments = 1
    } else if (recurring === 'yes') {
      delete plan.max_installments
    }

    if (recurring === 'no') {
      delete plan.interval
      delete plan.duration
      delete plan.interval_count
      plan.renewable = false
      plan.prorata = false
    } else {
      plan.prorata = true
    }

    try {
      await create(
        {
          ...plan,
          amount: cleanAmount(plan.amount),
          recurring: recurring === 'yes' ? true : false,
          ...otherAttributes
        },
        selectedCountry,
        productId || null,
        productType || PlanType.connectivity
      )
      toast.success('Plano criado com sucesso')
      navigate('/plans')
    } catch (err: any) {
      toast.error(
        err.suggestedMessage ?? 'Houve uma falha ao tentar criar o plano'
      )
    }
  }, [
    validateFieds,
    errors,
    history,
    plan,
    selectedCountry,
    recurring,
    productId,
    productType
  ])

  const onAttributeChange = useCallback(
    (attr: keyof Partial<Plan> | keyof Partial<PlanMeta>, value: any): void => {
      if (!plan) return

      const error = validate(attr as any, value, plan)

      if (attr === 'duration') {
        const penaltyError = plan?.penalty_period
          ? validate('penalty_period', String(plan.penalty_period), {
              duration: parseInt(value)
            })
          : null
        setPlan({ ...plan, [attr]: value })
        setErrors({ ...errors, duration: error, penalty_period: penaltyError })
        return
      }

      if (attr === 'nature' && value === Nature.good) {
        setRecurring('no')
      }

      if (attr === 'fine_amount') {
        value = formatCurrency(value)
      }

      setPlan({ ...plan, [attr]: value })

      setErrors({ ...errors, [attr]: error })
    },
    [errors, plan]
  )

  return (
    <>
      <PageHeader title="Planos" action="Novo" />
      <div className="flex flex-row space-x-8">
        <div className="w-3/5 px-4 animate-fade-in-down">
          <form
            onSubmit={(e) => {
              e.preventDefault()
              onSave()
            }}
          >
            <FormSection title="Novo plano">
              <SimpleCustomSelect
                label="País"
                options={countries}
                value={selectedCountry}
                onChange={(value) => setSelectedCountry(value)}
              />

              <ProductSearch
                filters={hardwareOrConnectivityActiveProductFilters}
                onSelect={(product: ReadResult<Product>) => {
                  setProductId(product.id)
                  const isHardware = product.attributes.type === 'hardware'
                  setProductType(
                    isHardware ? PlanType.hardware : PlanType.connectivity
                  )
                  setPlan((prev) => ({
                    ...prev,
                    nature: isHardware
                      ? hardwareNatures[0].value
                      : connectivityNatures[0].value
                  }))
                  if (!isHardware) setRecurring('yes')
                }}
              />

              <InputWithValidationError
                label="Nome"
                name="name"
                value={plan?.name}
                onChange={(name) => onAttributeChange('name', name)}
                error={errors.name}
              />

              <ResizibleTextArea
                label="Descrição"
                name="description"
                value={plan?.description}
                onChange={(description) =>
                  onAttributeChange('description', description)
                }
              />

              <fieldset className="space-y-5">
                <legend className="text-left">Estado de disponibilidade</legend>
                <CheckboxWithDescription
                  label="Ativado"
                  name="active"
                  checked={plan?.active}
                  onChange={(active) => onAttributeChange('active', active)}
                  description="Ao ativar o plano, todo o time da Softruck será notificado sobre sua criação ou edição."
                />
              </fieldset>
            </FormSection>

            <FormSection title="Estruturas de uso">
              <RadioListWithDescription
                label="Natureza"
                options={
                  productType === PlanType.hardware
                    ? hardwareNatures
                    : connectivityNatures
                }
                value={plan.nature}
                onChange={(nature) => onAttributeChange('nature', nature)}
              />

              <div>
                <div className="w-1/3">
                  <SimpleCustomSelect
                    label="Cobrança recorrente"
                    options={yesOrNo}
                    disabled={
                      plan.nature === Nature.good ||
                      productType === PlanType.connectivity
                    }
                    value={recurring}
                    onChange={(value) => setRecurring(value)}
                  />
                </div>
                <div className="text-xs text-gray mt-2">
                  {productType === PlanType.connectivity
                    ? 'Planos de Conectividade são obrigatoriamente recorrentes'
                    : 'Planos de natureza "Bem" são obrigatoriamente não recorrentes'}
                </div>
              </div>

              <RadioListWithDescription
                label="Tipo do plano"
                options={usageTypes}
                value={plan?.usage_type}
                onChange={(usageType) =>
                  onAttributeChange('usage_type', usageType)
                }
              />
            </FormSection>

            <FormSection title="Valores e Períodos">
              <div className="flex flex-row space-x-8">
                <div className="w-1/3">
                  <SimpleCustomSelect
                    label="Moeda"
                    options={currencies}
                    value={plan?.currency}
                    onChange={(currency) =>
                      onAttributeChange('currency', currency)
                    }
                  />
                </div>

                <div className="w-1/3">
                  <InputWithAddon
                    label="Valor"
                    name="amount"
                    type="text"
                    pattern="[0-9]+([\.,][0-9]+)?"
                    value={plan?.amount}
                    frontAddon={currency(plan?.currency)}
                    onChange={(value) => onAttributeChange('amount', value)}
                    error={errors.amount}
                  />
                </div>
              </div>

              {(recurring === 'no' ||
                (productType === PlanType.hardware &&
                  plan.nature === Nature.good)) && (
                <div className="flex flex-row space-x-8">
                  <div className="w-1/3">
                    <SimpleCustomSelect
                      label="Parcelável"
                      options={yesOrNo}
                      value={installmentFriendly}
                      onChange={(value) =>
                        setHardwareInstallmentFriendly(value)
                      }
                    />
                  </div>

                  <div className="w-1/3">
                    <InputWithValidationError
                      label="Número máximo de parcelas"
                      type="number"
                      name="quantity"
                      min="0"
                      max="120"
                      value={plan?.max_installments ?? 1}
                      onChange={(value) =>
                        onAttributeChange('max_installments', value)
                      }
                      error={errors.quantity}
                      onWheel={(e: React.FocusEvent<HTMLInputElement>) =>
                        e.target.blur()
                      }
                    />
                  </div>
                </div>
              )}

              {recurring === 'yes' && (
                <div className="flex flex-row space-x-8">
                  <div className="w-1/3">
                    <InputWithValidationError
                      label="Duração"
                      name="duration"
                      value={plan?.duration}
                      onChange={(duration) =>
                        onAttributeChange('duration', duration)
                      }
                      error={errors.duration}
                    />
                  </div>

                  <div className="w-1/3">
                    <SimpleCustomSelect
                      label="Intervalo"
                      options={intervals}
                      value={plan?.interval}
                      onChange={(interval) =>
                        onAttributeChange('interval', interval)
                      }
                    />
                  </div>

                  <div className="w-1/3">
                    <InputWithValidationError
                      label="Periodicidade"
                      name="interval_count"
                      value={plan?.interval_count}
                      onChange={(intervalCount) =>
                        onAttributeChange('interval_count', intervalCount)
                      }
                      error={errors.interval_count}
                    />
                  </div>
                </div>
              )}

              <RadioListWithDescription
                label="Modo de Fatura"
                options={billingTypes}
                value={plan?.billing_type}
                onChange={(billingType) =>
                  onAttributeChange('billing_type', billingType)
                }
              />

              {recurring === 'yes' && (
                <div className="flex flex-row space-x-8">
                  <div className="w-1/3">
                    <SimpleCustomSelect
                      label="Tipo de multa"
                      options={fineModes}
                      value={plan?.fine_mode}
                      onChange={(fineModes) =>
                        onAttributeChange('fine_mode', fineModes)
                      }
                    />
                  </div>

                  <div className="w-1/3">
                    <InputWithAddon
                      label="Valor da multa (por unidade)"
                      name="fine_amount"
                      type="text"
                      pattern="[0-9]+([\.,][0-9]+)?"
                      value={plan?.fine_amount}
                      frontAddon={
                        plan.fine_mode === 'fixed'
                          ? currency(plan?.currency)
                          : '%'
                      }
                      onChange={(value) =>
                        onAttributeChange('fine_amount', value)
                      }
                    />
                  </div>
                </div>
              )}

              {recurring === 'yes' &&
                plan?.fine_mode &&
                plan?.interval === 'month' && (
                  <div className="flex flex-col space-y-2">
                    <div className="w-1/3">
                      <InputWithValidationError
                        label={`Período de penalidade${
                          plan?.duration ? ` (máx. ${plan.duration} meses)` : ''
                        }`}
                        name="penalty_period"
                        type="text"
                        inputMode="numeric"
                        value={plan?.penalty_period ?? ''}
                        onChange={(value) =>
                          onAttributeChange(
                            'penalty_period',
                            value.replace(/\D/g, '') || null
                          )
                        }
                        error={errors.penalty_period}
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Durante esse período, multas de cancelamento são cobradas
                      normalmente. Após ele, o cancelamento não gera mais multa.{' '}
                      <span className="text-amber-600">
                        Se vazio, multas poderão ser cobradas a qualquer
                        momento, sem prazo de encerramento.
                      </span>
                    </p>
                  </div>
                )}
            </FormSection>

            <FormSection title="Restrições">
              <fieldset className="space-y-5">
                <legend className="text-left">Extensão de prazos</legend>
                <CheckboxWithDescription
                  label="Renovável"
                  name="renewable"
                  checked={recurring === 'yes' && plan?.renewable}
                  disabled={recurring === 'no'}
                  onChange={(renewable) =>
                    onAttributeChange('renewable', renewable)
                  }
                  description="Se o plano permite renovação este campo deverá ser ativado, lembrando que comodatos de rastreadores não devem ter essa condição como ativada"
                />
              </fieldset>

              <fieldset className="space-y-5">
                <legend className="text-left">Aplicação de descontos</legend>
                <CheckboxWithDescription
                  label="Restrito"
                  name="restricted"
                  checked={plan?.restricted}
                  onChange={(restricted) =>
                    onAttributeChange('restricted', restricted)
                  }
                  description="Planos restritos não podem ser aplicados nenhum tipo de desconto"
                />
              </fieldset>

              <fieldset className="space-y-5">
                <legend className="text-left">Definição de garantias</legend>
                <CheckboxWithDescription
                  label="Devedor solidário"
                  name="guarantors"
                  checked={plan?.guarantors}
                  onChange={(guarantors) =>
                    onAttributeChange('guarantors', guarantors)
                  }
                  description="Planos de hardware em sua maioria necessitam de uma segurança financeira para serem vendidos"
                />
              </fieldset>

              <fieldset className="space-y-5">
                <legend className="text-left">Definições Logísticas</legend>
                <CheckboxWithDescription
                  disabled
                  label="Necessita de suporte logístico"
                  name="logistics"
                  checked={plan?.logistics}
                  onChange={(logistics) =>
                    onAttributeChange('logistics', logistics)
                  }
                  description="Escolha esta opção se o plano necessita de etapas de construção internas"
                />
              </fieldset>

              <fieldset className="space-y-5">
                <legend className="text-left">Dados fiscais</legend>
                <CheckboxWithDescription
                  disabled={productType === PlanType.hardware}
                  label="Necessita de dados fiscais"
                  name="fiscal_data"
                  checked={plan?.fiscal_data}
                  onChange={(fiscalData) =>
                    onAttributeChange('fiscal_data', fiscalData)
                  }
                  description="Escolha esta opção se o plano necessita de etapas de formalizações fiscais"
                />
              </fieldset>
            </FormSection>

            <div className="mt-12 flex justify-end">
              <button
                type="button"
                className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                onClick={() => navigate(-1)}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 focus:outline-none"
              >
                Salvar
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
